import {
  getInstrumentId,
  isDefined,
  ListedInstrument,
  useDefaultOrderPricesCommon,
  useInstrumentStoreItem,
} from '@eih-netbroker/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { instrumentStore } from '../../../../shared/data/instrumentStore';
import { useEStream } from '../../../../shared/services/sse/useEStream';
import { useInstrumentSearch } from '../../../../shared/services/useInstrumentSearch';
import { useLogger } from '../../../../shared/utils/useLogger';
import { OrderFormData } from '../../components/order-new/order-form/OrderForm2';

const defaultInitTimeoutInMS = 5000;

export function useDefaultOrderPrices(orderForm: OrderFormData) {
  const { instrumentTickSize, getInstrumentTickSizeOfList, getTickSizeForInstrument } = useInstrumentSearch();
  const { subscribeToInstrumentChanges } = useEStream();
  const [areDefaultPricesInitializing, setAreDefaultPricesInitializing] = useState(true);
  const instrumentStoreItem = useInstrumentStoreItem(
    orderForm.instrument ?? null,
    instrumentStore,
    subscribeToInstrumentChanges,
    {
      source: 'useDefaultOrderPricesWeb',
    },
  );
  const initTimer = useRef<NodeJS.Timer | null>(null);
  const initializingInstrumentIdRef = useRef<string | undefined>(undefined);
  const logger = useLogger('useDefaultOrderPricesWeb');

  const p = useMemo(
    () => ({
      orderForm,
      instrumentStoreItem,
      instrumentTickSize,
      getInstrumentTickSizeOfList,
    }),
    [orderForm, instrumentStoreItem, instrumentTickSize, getInstrumentTickSizeOfList],
  );

  const { defaultActivationPrice, defaultLimitPrice, tickSize } = useDefaultOrderPricesCommon(p);
  const selectedInstrumentId = orderForm.instrument ? getInstrumentId(orderForm.instrument) : undefined;
  const isCurrentInstrumentInitializing =
    isDefined(selectedInstrumentId) &&
    (areDefaultPricesInitializing || initializingInstrumentIdRef.current !== selectedInstrumentId);

  const clearTimer = useCallback(() => {
    if (initTimer.current !== null) {
      clearTimeout(initTimer.current);
      initTimer.current = null;
    }
  }, []);

  const refreshDefaultPrices = useCallback(
    async (instrument: ListedInstrument | null) => {
      if (!isDefined(instrument)) {
        initializingInstrumentIdRef.current = undefined;
        setAreDefaultPricesInitializing(false);
        clearTimer();
        return;
      }

      const instrumentId = getInstrumentId(instrument);
      initializingInstrumentIdRef.current = instrumentId;
      setAreDefaultPricesInitializing(true);
      clearTimer();
      initTimer.current = setTimeout(() => {
        if (initializingInstrumentIdRef.current === instrumentId) {
          setAreDefaultPricesInitializing(false);
          initTimer.current = null;
          logger.warn(`Default prices initialization timed out for instrument ${instrumentId}`);
        }
      }, defaultInitTimeoutInMS);

      try {
        await getTickSizeForInstrument(instrument);
      } catch (e) {
        logger.error('Error fetching tick size for instrument', e);
      }
    },
    [clearTimer, getTickSizeForInstrument, logger],
  );

  useEffect(() => {
    if (
      !areDefaultPricesInitializing ||
      initializingInstrumentIdRef.current !== selectedInstrumentId ||
      !isDefined(instrumentStoreItem?.price?.value) ||
      !isDefined(tickSize)
    ) {
      return;
    }

    setAreDefaultPricesInitializing(false);
    clearTimer();
  }, [
    areDefaultPricesInitializing,
    clearTimer,
    instrumentStoreItem?.price?.value,
    orderForm.instrument,
    tickSize,
    selectedInstrumentId,
  ]);

  return {
    defaultActivationPrice: isCurrentInstrumentInitializing ? undefined : defaultActivationPrice,
    defaultLimitPrice: isCurrentInstrumentInitializing ? undefined : defaultLimitPrice,
    areDefaultPricesInitializing: isCurrentInstrumentInitializing,
    refreshDefaultPrices,
  };
}
